import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import axios from 'axios';
import _ from 'lodash';
import {
  Typography,
  Button,
  IconButton,
  Snackbar,
  SnackbarContent,
  Tooltip,
} from '@material-ui/core';
import {
  MdFilterList, MdClose, MdEdit, MdFileDownload,
} from 'react-icons/lib/md';
import { Redirect } from 'react-router-dom';

import { getCandidatesList } from '../../services/selectionStagesService';
import { shortlistCandidates } from '../../services/shortlistService';
import blockProps from '../../utils/blockProps';
import AppShell from '../../components/AppShell';
import Loader from '../../components/Loader';
import DataTable from '../../components/DataTable';
import ModalFilter from '../../components/ModalFilter';
import GroupDiscussion from '../../components/GroupDiscussion';
import Auth from '../../components/Auth';
import ModalForm from '../../components/ModalForm';
import ModalConfirmation from '../../components/ModalConfirmation';

class SelectionStages extends React.Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.object,
    }).isRequired,
    history: PropTypes.object.isRequired,
    authenticatedUserInfo: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      dataLoaded: false,
      stage: '',
      candidatesList: [],
      headers: [],
      error: null,
      loading: false,
      snackbarOpened: false,
      snackbarMessage: '',
      shortlistError: false,
      filterConfig: {},
    };

    this.dataTableRef = null;
  }

  componentDidMount() {
    this.unsubscribe = getCandidatesList((candidatesList) => {
      this.setState({ candidatesList });
    });

    this.fetchDatatableHeaders();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.stageId !== this.props.match.params.stageId) {
      this.fetchDatatableHeaders();
    }
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  getFilteredCandidatesList = () => {
    const candidateList = this.props.authenticatedUserInfo.type === 'CITY_IMPACT_MANAGER'
      ? _.filter(
        this.state.candidatesList,
        candidateData => candidateData.jypCity === this.props.authenticatedUserInfo.location,
      )
      : this.state.candidatesList;
    const stageBasedCandidateList = _.filter(
      candidateList,
      candidate => candidate.stagesSelected[this.state.stage],
    );

    let filteredCandidates = stageBasedCandidateList;
    const filterKeys = Object.keys(this.state.filterConfig);
    if (filterKeys.length > 0) {
      filteredCandidates = _.filter(stageBasedCandidateList, candidateData => filterKeys
        .map((filterKey) => {
          const filterData = this.state.filterConfig[filterKey];
          if (filterData.lookupType === 'equal') {
            if (Array.isArray(filterData.value)) {
              return filterData.value.includes(_.get(candidateData, filterKey));
            }
            return _.get(candidateData, filterKey) === filterData.value;
          }
          if (filterData.lookupType === 'geq') {
            return _.get(candidateData, filterKey) >= filterData.value;
          }
          if (filterData.lookupType === 'leq') {
            return _.get(candidateData, filterKey) <= filterData.value;
          }
          return false;
        })
        .every(val => val));
    }

    const modifiedData = filteredCandidates.map(candidateData => ({
      ...candidateData,
      hearAbout: candidateData.hearAbout.split('_').join(' '),
    }));

    return modifiedData;
  };

  fetchDatatableHeaders = async () => {
    const { stageId } = this.props.match.params;
    this.setState({ dataLoaded: false, error: null });

    try {
      const { data } = await axios.options(
        `https://us-central1-jyp-admin-saas.cloudfunctions.net/datatable/?stage=${stageId}`,
      );
      const response = data.data || [];

      this.setState({ dataLoaded: true, headers: response, stage: stageId });
    } catch (error) {
      this.setState({ dataLoaded: true, error: error.message, stage: stageId });
    }
  };

  handleSnackbarClose = () => {
    this.setState({
      snackbarOpened: false,
      snackbarMessage: '',
      shortlistError: false,
    });
  };

  shortListCandidates = async (selectedCandidatesId, nextStage) => {
    this.setState({ loading: true });
    const updateData = {
      ids: selectedCandidatesId,
      shortlistStage: nextStage,
    };
    try {
      const { data } = await shortlistCandidates(updateData);
      const response = data || {};

      this.setState(
        { loading: false, snackbarOpened: true, snackbarMessage: response.message },
        () => {
          if (this.dataTableRef) {
            this.dataTableRef.resetSelectedRows();
          }
          if (this.props.history.push(`/selection-stage/${nextStage}`));
        },
      );
    } catch (error) {
      const errors = _.get(error, 'response.data.data.errors');
      const errorMessages = errors
        ? 'Unable to shortlist some candidates due to incomplete scores'
        : error.message;
      const failedIds = _.get(error, 'response.data.data.failedIds', []);

      this.setState(
        {
          loading: false,
          shortlistError: true,
          snackbarOpened: true,
          snackbarMessage: errorMessages,
        },
        () => {
          if (this.dataTableRef) {
            this.dataTableRef.resetSelectedRows(failedIds);
          }
        },
      );
    }
  };

  rejectCandidates = async (selectedCandidatesId, currentStage) => {
    this.setState({ loading: true });
    const updateData = {
      ids: selectedCandidatesId,
      currentStage,
    };
    try {
      const { data } = await axios.post(
        'https://us-central1-jyp-admin-saas.cloudfunctions.net/reject/',
        updateData,
      );
      const response = data.data || {};

      this.setState(
        { loading: false, snackbarOpened: true, snackbarMessage: response.message },
        () => {
          if (this.dataTableRef) {
            this.dataTableRef.resetSelectedRows();
          }
        },
      );
    } catch (error) {
      const errorMessages = error.response && error.response.data && error.response.data.errors;
      this.setState({
        loading: false,
        shortlistError: true,
        snackbarOpened: true,
        snackbarMessage: errorMessages || error.message,
      });
    }
  };

  renderStageBasedActions = (selectedRows) => {
    if (this.state.stage === 'registration') {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            this.shortListCandidates(selectedRows, 'knowledgeTest');
          }}
        >
          Shortlist
        </Button>
      );
    }
    if (this.state.stage === 'knowledgeTest') {
      return [
        <Button
          key="shortlist"
          variant="contained"
          color="primary"
          onClick={() => {
            this.shortListCandidates(selectedRows, 'groupDiscussion');
          }}
        >
          Shortlist
        </Button>,
        <ModalConfirmation
          key="reject"
          tooltipTitle="Reject"
          trigger={(
            <Button variant="contained" color="secondary">
              Reject
            </Button>
)}
          onConfirm={() => {
            this.rejectCandidates(selectedRows, 'knowledgeTest');
          }}
        />,
      ];
    }
    if (this.state.stage === 'groupDiscussion') {
      return [
        <Button
          key="shortlist"
          variant="contained"
          color="primary"
          onClick={() => {
            this.shortListCandidates(selectedRows, 'researchActivity');
          }}
        >
          Shortlist
        </Button>,
        <ModalConfirmation
          key="reject"
          tooltipTitle="Reject"
          trigger={(
            <Button variant="contained" color="secondary">
              Reject
            </Button>
)}
          onConfirm={() => {
            this.rejectCandidates(selectedRows, 'groupDiscussion');
          }}
        />,
        selectedRows.length === 1 && (
          <ModalForm
            key="edit-gd"
            tooltipTitle="Edit GD Info"
            trigger={(
              <IconButton aria-label="Edit GD Info">
                <MdEdit size={24} />
              </IconButton>
)}
            apiEndpoint={`https://us-central1-jyp-admin-saas.cloudfunctions.net/updateCandidate/${
              selectedRows[0]
            }/?stage=groupDiscussion`}
            method="put"
            onClose={() => {
              if (this.dataTableRef) {
                this.dataTableRef.resetSelectedRows();
              }
            }}
          />
        ),
      ];
    }
    if (this.state.stage === 'personalInterview') {
      return [
        selectedRows.length === 1 && (
          <ModalForm
            key="edit-pi"
            tooltipTitle="Edit Personal Interview"
            trigger={(
              <IconButton aria-label="Edit Personal Interview">
                <MdEdit size={24} />
              </IconButton>
)}
            apiEndpoint={`https://us-central1-jyp-admin-saas.cloudfunctions.net/updateCandidate/${
              selectedRows[0]
            }/?stage=personalInterview`}
            method="put"
            onClose={() => {
              if (this.dataTableRef) {
                this.dataTableRef.resetSelectedRows();
              }
            }}
          />
        ),
      ];
    }
    if (this.state.stage === 'researchActivity') {
      return [
        this.props.authenticatedUserInfo.type !== 'CITY_IMPACT_MANAGER' && (
          <Button
            key="shortlist"
            variant="contained"
            color="primary"
            onClick={() => {
              this.shortListCandidates(selectedRows, 'selected');
            }}
          >
            Shortlist
          </Button>
        ),
        this.props.authenticatedUserInfo.type !== 'CITY_IMPACT_MANAGER' && (
          <ModalConfirmation
            key="reject"
            tooltipTitle="Reject"
            trigger={(
              <Button variant="contained" color="secondary">
                Reject
              </Button>
)}
            onConfirm={() => {
              this.rejectCandidates(selectedRows, 'researchActivity');
            }}
          />
        ),
        selectedRows.length === 1 && (
          <ModalForm
            key="edit-ra"
            tooltipTitle="Edit Research Activity"
            trigger={(
              <IconButton aria-label="Edit Research Activity">
                <MdEdit size={24} />
              </IconButton>
)}
            apiEndpoint={`https://us-central1-jyp-admin-saas.cloudfunctions.net/updateCandidate/${
              selectedRows[0]
            }/?stage=researchActivity`}
            method="put"
            onClose={() => {
              if (this.dataTableRef) {
                this.dataTableRef.resetSelectedRows();
              }
            }}
          />
        ),
      ];
    }
    if (this.state.stage === 'selected') {
      return (
        <ModalConfirmation
          key="reject"
          tooltipTitle="Reject"
          trigger={(
            <Button variant="contained" color="secondary">
              Reject
            </Button>
)}
          onConfirm={() => {
            this.rejectCandidates(selectedRows, 'selected');
          }}
        />
      );
    }

    return null;
  };

  renderToolbars = (selectedRows) => {
    if (selectedRows.length > 0) {
      return <ToolbarContainer>{this.renderStageBasedActions(selectedRows)}</ToolbarContainer>;
    }
    return (
      <ToolbarContainer>
        {this.state.stage === 'registration' && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              const filterConfigQueryString = JSON.stringify(this.state.filterConfig);
              const downloadUrl = encodeURI(
                `https://us-central1-jyp-admin-saas.cloudfunctions.net/testResponse/quiz/?filters=${filterConfigQueryString}`,
              );
              window.open(downloadUrl, '_blank');
            }}
          >
            Quiz Response
          </Button>
        )}

        {this.state.stage === 'knowledgeTest' && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              const filterConfigQueryString = JSON.stringify(this.state.filterConfig);
              const downloadUrl = encodeURI(
                `https://us-central1-jyp-admin-saas.cloudfunctions.net/testResponse/knowledgeTest/?filters=${filterConfigQueryString}`,
              );
              window.open(downloadUrl, '_blank');
            }}
          >
            KT Response
          </Button>
        )}

        <ModalFilter
          apiEndpoint={`https://us-central1-jyp-admin-saas.cloudfunctions.net/filters/?stage=${
            this.state.stage
          }`}
          trigger={(
            <IconButton aria-label="Filter list">
              <MdFilterList />
            </IconButton>
)}
          tooltipTitle="Filter"
          onApply={(filterConfig) => {
            this.setState({ filterConfig });
          }}
          onClear={() => {
            this.setState({ filterConfig: {} });
          }}
          initialValues={this.state.filterConfig}
        />
        <Tooltip title="Download CSV">
          <IconButton
            aria-label="Download CSV"
            onClick={() => {
              const filterConfigQueryString = JSON.stringify(this.state.filterConfig);
              const downloadUrl = encodeURI(
                `https://us-central1-jyp-admin-saas.cloudfunctions.net/downloadCsv/?stage=${
                  this.state.stage
                }&filters=${filterConfigQueryString}`,
              );

              window.open(downloadUrl, '_blank');
            }}
          >
            <MdFileDownload />
          </IconButton>
        </Tooltip>
      </ToolbarContainer>
    );
  };

  render() {
    const { authenticatedUserInfo } = this.props;

    if (!authenticatedUserInfo) {
      return null;
    }

    if (
      authenticatedUserInfo.type === 'CITY_IMPACT_MANAGER'
      && this.props.match.params.stageId !== 'researchActivity'
    ) {
      return (
        <Redirect
          to={{
            pathname: '/404',
          }}
        />
      );
    }

    return (
      <div>
        <AppShell>
          {this.state.error ? (
            <Typography variant="subheading" color="error">
              {this.state.error}
            </Typography>
          ) : (
            <div>
              {!this.state.dataLoaded ? (
                <Loader />
              ) : (
                <div>
                  {this.props.match.params.stageId === 'groupDiscussion' && (
                    <GroupDiscussionContainer>
                      <GroupDiscussion />
                    </GroupDiscussionContainer>
                  )}
                  <DataTable
                    ref={(comp) => {
                      this.dataTableRef = comp;
                    }}
                    title={this.state.stage
                      .split(/(?=[A-Z])/)
                      .map(str => _.capitalize(str))
                      .join(' ')}
                    subtitle="Candidates"
                    tableHeaders={this.state.headers}
                    tableData={this.getFilteredCandidatesList()}
                    // eslint-disable-next-line max-len
                    renderToolbars={selectedRows => this.renderToolbars(selectedRows)}
                  />
                </div>
              )}
            </div>
          )}
        </AppShell>

        <Snackbar
          open={this.state.snackbarOpened}
          autoHideDuration={2000}
          onClose={this.handleSnackbarClose}
        >
          <StyledSnackbarContent
            shortlistError={this.state.shortlistError}
            message={this.state.snackbarMessage}
            action={[
              <IconButton
                key="close"
                aria-label="Close"
                color="inherit"
                onClick={this.handleSnackbarClose}
              >
                <MdClose size={20} />
              </IconButton>,
            ]}
          />
        </Snackbar>

        {this.state.loading && <Loader />}
      </div>
    );
  }
}

export default props => (
  <Auth>{({ authState }) => <SelectionStages {...props} {...authState} />}</Auth>
);

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;

  & > * + * {
    margin-left: 10px !important;
  }
`;

const StyledSnackbarContent = styled(blockProps(SnackbarContent, ['shortlistError']))`
  background-color: ${props => (props.shortlistError ? 'red' : 'green')} !important;
`;

const GroupDiscussionContainer = styled.div`
  margin-bottom: 32px;
`;



// WEBPACK FOOTER //
// ./src/pages/SelectionStages/SelectionStages.js